import React, { useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { validateRegisterId, validateJoinPassword, validatePasswordConfirm } from '../../../utils/validation';
import { joinUser } from "../../../services/Join/join";
import PopupLoginError from "../../../components/Popup/LoginError";
import { useLanguage } from "../../../hooks/useLanguage";
import EyeIconOff from '../../../assets/icon/icon-off-show.png';
import EyeIconOn from '../../../assets/icon/icon-on-show.png';

function JoinUser(){
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const[isPopupLoginError, setIsPopupLoginError] = useState("")

  // 앱 환경 감지 함수 추가
  const isInApp = () => {
    const userAgent = navigator.userAgent || '';
    return (
      /DayPay|daypay|AppWebView|wv|inapp|app/i.test(userAgent) ||
      typeof window.ReactNativeWebView !== 'undefined' ||
      typeof window.AppInterface !== 'undefined' ||
      window.location.href.includes('app=true')
    );
  };

  const [formData, setFormData] = useState({
    userId: '',
    password: '',
    confirmPassword: ''
  });

  const [showErrors, setShowErrors] = useState({
    userId: false,
    password: false,
    confirmPassword: false
  });

  // 상태 추가
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 토글 함수 추가
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // 아이디 입력 핸들러
  const handleUserIdChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, userId: value }));
    setShowErrors(prev => ({ ...prev, userId: value !== '' }));
  };

  // 비밀번호 입력 핸들러
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, password: value }));
    setShowErrors(prev => ({ ...prev, password: value !== '' }));
  };

  // 비밀번호 확인 입력 핸들러
  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, confirmPassword: value }));
    setShowErrors(prev => ({ ...prev, confirmPassword: value !== '' }));
  };

  // 폼 유효성 검사
  const isFormValid = () => {
    const isIdValid = validateRegisterId(formData.userId).isValid;
    const isPasswordValid = validateJoinPassword(formData.password).isValid;
    const isConfirmPasswordValid = validatePasswordConfirm(formData.password, formData.confirmPassword).isValid;

    return isIdValid && isPasswordValid && isConfirmPasswordValid;
  };

  // 다음 버튼 클릭 핸들러 수정
  const handleNextClick = async () => {
    try {
      let prevFormData, files;
      
      // 앱 환경에서는 localStorage에서 데이터 불러오기
      if (isInApp()) {
        const storedFormData = localStorage.getItem('joinFormData');
        const storedFiles = localStorage.getItem('joinFiles');
        
        if (!storedFormData) {
          alert('저장된 회원가입 데이터가 없습니다!');
          navigate('/joinForm');
          return;
        }
        
        prevFormData = JSON.parse(storedFormData);
        
        // 파일 데이터 처리
        files = {};
        if (storedFiles) {
          const fileData = JSON.parse(storedFiles);
          
          // Base64 데이터를 File 객체로 변환
          fileData.forEach(file => {
            const byteCharacters = atob(file.data.split(',')[1]);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: file.type });
            files[file.key] = new File([blob], file.name, { type: file.type });
          });
        }
        
      } else {
        // PC/웹 환경에서는 기존 방식 (location.state 사용)
        if (!location.state) {
          alert('회원가입 정보가 없습니다!');
          navigate('/joinForm');
          return;
        }
        
        prevFormData = location.state.formData;
        files = location.state.files;
      }

      // 제출할 데이터 구성
      const submitData = {
        mailing: prevFormData.mailing,
        sms: prevFormData.sms,
        push: prevFormData.push,
        push_id: prevFormData.push_id,
        ci: prevFormData.ci,
        di: prevFormData.di,
        username: prevFormData.username,
        mem_phone: prevFormData.mem_phone,
        mem_phone2: prevFormData.mem_phone2,
        mem_type: prevFormData.mem_type,
        email: prevFormData.email,
        mem_zipcode: prevFormData.mem_zipcode,
        mem_address_street: prevFormData.mem_address_street,
        mem_address_detail: prevFormData.mem_address_detail,
        userid: formData.userId,
        password: formData.password,
        business_name: prevFormData.business_name,
        business_num: prevFormData.business_num,
        business_ceo: prevFormData.business_ceo,
        business_category: prevFormData.business_category,
        business_item: prevFormData.business_item,
        member_kind: prevFormData.member_kind,
        mem_gender: prevFormData.mem_gender,
        mem_birthday: prevFormData.mem_birthday
      };

      // 제작사인 경우 사업자 정보 추가
      if (prevFormData.mem_type === "4" && prevFormData.business_info) {
        Object.assign(submitData, prevFormData.business_info);
      }

      const response = await joinUser(submitData, files);
      
      // 응답 데이터 확인
      if (response.data) {
        if (response.data.code === 200) {
          // 성공 시 localStorage 정리 (앱 환경인 경우)
          if (isInApp()) {
            localStorage.removeItem('joinFormData');
            localStorage.removeItem('joinFiles');
          }
          navigate('/joinComplete');
        } else {
          setIsPopupLoginError(response.data.message || "회원가입 처리 중 오류가 발생했습니다.");
        }
      }
    } catch (error) {
      console.error('회원가입 처리 중 오류:', error);
      
      // 타임아웃 에러 특별 처리
      if (error.code === 'ECONNABORTED') {
        setIsPopupLoginError("서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.");
      } else if (error.response?.data?.message) {
        setIsPopupLoginError(error.response.data.message);
      } else {
        setIsPopupLoginError("회원가입 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
      }
    }
  };

  return(
    <div>
      <section className="auth_section">
        <div className="inner gap_32">
          <div className="section_title">
            <h3>{t('sign_up')}</h3>
          </div>
          <div className="form_wrap join_con">
            <h6 className="sub_title">{t('id_password')}</h6>
            <div className="form_group">
              {/* 아이디 입력 */}
              <div className="default_input flex-8 column">
                <label htmlFor="member_id">{t('id')}</label>
                <div>
                  <input
                    id="member_id"
                    maxLength="16"
                    placeholder={t('id_rule')}
                    type="text"
                    value={formData.userId}
                    onChange={handleUserIdChange}
                  />
                  {showErrors.userId && !validateRegisterId(formData.userId).isValid && (
                    <span className="guide_text red">
                      {t('id_rule_instruction')}
                    </span>
                  )}
                </div>
              </div>

              {/* 비밀번호 입력 */}
              <div className="default_input flex-8 column">
                <label htmlFor="member_pw">{t('password')}</label>
                <div className="password_input_wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="member_pw"
                    maxLength="16"
                    placeholder={t('password_rule')}
                    value={formData.password}
                    onChange={handlePasswordChange}
                  />
                  <button
                    type="button"
                    className="password_toggle_btn"
                    onClick={togglePasswordVisibility}
                  >
                    <img 
                      src={showPassword ? EyeIconOn : EyeIconOff} 
                      alt="비밀번호 표시/숨김"
                      width="20"
                      height="20"
                    />
                  </button>
                </div>
                {showErrors.password && !validateJoinPassword(formData.password).isValid && (
                  <span className="guide_text red">
                    {t('password_rule_instruction')}
                  </span>
                )}
              </div>

              {/* 비밀번호 확인 */}
              <div className="default_input flex-8 column re_password">
                <label htmlFor="member_confirm_pw">{t('password_confirm')}</label>
                <div className="password_input_wrapper">
                  <input
                    id="member_confirm_pw"
                    placeholder={t('password_rule_txt')}
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleConfirmPasswordChange}
                  />
                  <button
                    type="button"
                    className="password_toggle_btn"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    <img 
                      src={showConfirmPassword ? EyeIconOn : EyeIconOff} 
                      alt="비밀번호 표시/숨김"
                      width="20"
                      height="20"
                    />
                  </button>
                </div>
                {showErrors.confirmPassword && !validatePasswordConfirm(formData.password, formData.confirmPassword).isValid && (
                  <span className="guide_text red">
                    {t('password_mismatch')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button 
            type="button" 
            className={`default_btn ${isFormValid() ? 'black' : 'gray2'} lg`}
            onClick={handleNextClick}
            disabled={!isFormValid()}
          >
            {t('next')}
          </button>
        </div>
        <PopupLoginError isPopup={isPopupLoginError} setIsPopup={setIsPopupLoginError}/>
      </section>
    </div>
  );
}

export default JoinUser;